import { useEffect } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import SectionHeader from "@/components/feature/SectionHeader";
import Reviews from "@/pages/home/components/Reviews";

const principles = [
  { icon: "ri-globe-line", title: "定位清晰", desc: "专注韩国妖精漫画的奇幻魔法、异世界、精灵与妖怪神话题材，不贪多求全，只做精而专的选漫服务。" },
  { icon: "ri-search-eye-line", title: "简介对味", desc: "坚持用不剧透又引人入胜的简介呈现作品，帮助读者快速判断是否值得入坑，减少盲选踩雷。" },
  { icon: "ri-sparkling-fill", title: "分类导航", desc: "以世界观设定为纲，划分异世界穿越、精灵契约、妖怪神话三大分类，让找作品又快又准。" },
  { icon: "ri-hand-heart-line", title: "尊重原创", desc: "我们尊重并支持创作者的正版权益，网站定位为资讯与推荐平台，助力好作品被更多人看到。" },
];

const milestones = [
  { year: "2021", title: "初心萌芽", desc: "从对奇幻题材的热爱出发，着手整理妖精漫画相关的分类与内容资讯。" },
  { year: "2022", title: "分类成型", desc: "逐步梳理出异世界、精灵、妖怪三大世界观分类，形成稳定的内容框架。" },
  { year: "2023", title: "内容深耕", desc: "上线题材解析与选漫指南，让读者不仅追更，更能看懂作品背后的世界观魅力。" },
  { year: "2024", title: "持续陪伴", desc: "保持每周更新速览与热度榜单，陪伴每一位幻想爱好者长期追更。" },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = "关于我们 · 妖精漫画堂 韩国妖精漫画奇幻异世界在线看";
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于妖精漫画堂",
    description: "妖精漫画堂是一家专注韩国妖精漫画奇幻魔法、异世界穿越、精灵契约与妖怪神话题材的漫画资讯与推荐网站。",
    inLanguage: "zh-CN",
    isPartOf: { "@type": "WebSite", name: "妖精漫画堂" },
  };

  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="关于妖精漫画堂"
        description="一个为奇幻幻想爱好者而生的韩国妖精漫画选漫平台，专注异世界、精灵与妖怪神话，陪你追遍每一部值得一看的魔法世界。"
        image="https://readdy.ai/api/search-image?query=open%20magical%20book%20with%20glowing%20fairy%20spirits%20rising%20and%20a%20library%20of%20fantasy%20worlds%2C%20stylized%20digital%20painting%2C%20emerald%20and%20gold%20palette&width=1600&height=600&seq=about-banner&orientation=landscape"
        crumbs={[
          { label: "首页", to: "/" },
          { label: "关于我们" },
        ]}
      />

      <section className="py-12 md:py-16">
        <div className="container-site grid items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-accent-600">
              <i className="ri-sparkling-2-fill" />
              品牌故事
            </span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">从一部漫画开始，到整个奇幻世界</h2>
            <div className="mt-4 space-y-3 text-sm leading-7 text-foreground-700">
              <p>
                妖精漫画堂诞生于对奇幻题材的一份纯粹热爱。我们发现，当一部作品拥有精灵、妖怪、神祇与魔法世界这样迷人的设定时，它值得被更认真地对待、被更清晰地呈现。于是，我们决定做一个专注于韩国妖精漫画的选漫平台。
              </p>
              <p>
                我们以世界观设定为核心，将纷繁的作品梳理成异世界穿越、精灵契约、妖怪神话三大分类，用对味的简介帮助每一位读者快速锁定心仪的故事。无论是初入奇幻的新朋友，还是阅漫无数的老书虫，都能在这里找到属于自己的那部作品。
              </p>
              <p>
                我们相信，好的奇幻故事拥有跨越现实的力量。而妖精漫画堂的使命，就是当好这座连接幻想与读者的桥。
              </p>
            </div>
            <Link to="/contact" className="btn btn-primary mt-6">
              联系我们
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=fairy%20readers%20browsing%20magical%20comic%20books%20among%20glowing%20plants%20in%20a%20dreamy%20library%2C%20stylized%20digital%20illustration%2C%20emerald%20and%20warm%20gold%20tones&width=1000&height=750&seq=about-story&orientation=landscape"
              alt="妖精漫画堂的品牌愿景插画"
              loading="lazy"
              className="w-full rounded-lg object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100 py-14 md:py-20">
        <div className="container-site">
          <SectionHeader
            align="center"
            eyebrow="内容原则"
            title="我们坚持的四件事"
            subtitle="清晰的定位、对味的简介、用心的分类与对原创的尊重，是我们始终如一的底线。"
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <div key={p.title} className="card p-6 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className={`${p.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="container-site">
          <SectionHeader
            eyebrow="发展历程"
            title="一步步走来，只为更好"
            subtitle="从整理第一份分类到搭建完整的选漫体系，我们一直在路上。"
          />
          <ol className="relative grid gap-6 border-l border-background-200 pl-6 md:grid-cols-4 md:border-l-0 md:pl-0 md:pt-6">
            {milestones.map((m, i) => (
              <li key={m.year} className="relative">
                <span className="absolute -left-[1.9rem] top-0.5 flex h-4 w-4 items-center justify-center md:-top-[0.15rem] md:left-1/2 md:-translate-x-1/2">
                  <span className="h-3.5 w-3.5 rounded-full border-2 border-background-50 bg-accent-500" />
                </span>
                <div className="card p-5">
                  <span className="font-heading text-2xl font-bold text-accent-600">{m.year}</span>
                  <h3 className="mt-1.5 font-heading text-base font-semibold text-foreground-900">{m.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{m.desc}</p>
                  {i === milestones.length - 1 && <span className="mt-2 inline-block rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-700">进行中</span>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <Reviews />

      <section className="pb-14 md:pb-20">
        <div className="container-site">
          <div className="flex flex-col items-center justify-between gap-5 rounded-lg bg-primary-50 p-7 text-center md:flex-row md:text-left">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">和我们聊聊你爱的妖精漫画</h2>
              <p className="mt-1.5 text-sm text-foreground-600">投稿、建议或单纯分享喜爱，我们都很欢迎。</p>
            </div>
            <Link to="/contact" className="btn btn-primary shrink-0">
              前往联系
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </>
  );
}